const REQUIRED = [
  "slug",
  "name",
  "short",
  "tagline",
  "founded",
  "trust",
  "entities",
  "spreadModel",
  "eurUsdSpread",
  "commission",
  "minDeposit",
  "swapFree",
  "platforms",
  "funding",
  "withdrawalTime",
  "withdrawalFee",
  "executionMs",
  "tripleSwapDay",
  "swaps",
  "pros",
  "cons",
  "referral",
];

/** Client-side mirror of backend validation for instant upload feedback. */
export function validateBrokerJson(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return { ok: false, error: "Root value must be a JSON object (one broker)." };
  }

  const missing = REQUIRED.filter((k) => !(k in payload));
  if (missing.length) {
    return { ok: false, error: `Missing required fields: ${missing.join(", ")}` };
  }

  if (typeof payload.slug !== "string" || !payload.slug.trim()) {
    return { ok: false, error: "slug must be a non-empty string" };
  }

  if (!Array.isArray(payload.entities) || payload.entities.length < 1) {
    return { ok: false, error: "entities must be a non-empty array" };
  }

  for (let i = 0; i < payload.entities.length; i += 1) {
    const ent = payload.entities[i];
    if (!ent || typeof ent !== "object") {
      return { ok: false, error: `entities[${i}] must be an object` };
    }
    for (const key of ["name", "regulator", "jurisdiction", "regions", "leverage"]) {
      if (!(key in ent)) return { ok: false, error: `entities[${i}] missing '${key}'` };
    }
    if (!Array.isArray(ent.regions)) {
      return { ok: false, error: `entities[${i}].regions must be an array` };
    }
  }

  if (!payload.swaps || typeof payload.swaps !== "object" || Array.isArray(payload.swaps)) {
    return { ok: false, error: "swaps must be an object keyed by instrument id" };
  }

  for (const key of ["platforms", "funding", "pros", "cons"]) {
    if (!Array.isArray(payload[key])) {
      return { ok: false, error: `${key} must be an array` };
    }
  }

  for (const key of ["eurUsdSpread", "commission", "minDeposit", "trust", "founded", "executionMs", "tripleSwapDay"]) {
    if (typeof payload[key] !== "number") {
      return { ok: false, error: `${key} must be a number` };
    }
  }

  if (typeof payload.swapFree !== "boolean") {
    return { ok: false, error: "swapFree must be a boolean" };
  }

  const slug = payload.slug.trim().toLowerCase().replace(/\s+/g, "-");
  const broker = {
    ...payload,
    slug,
    logo: payload.logo || `/brokers/${slug}.png`,
    swapFreeNote: payload.swapFreeNote ?? "",
    depositBonus: payload.depositBonus ?? null,
    swapUpdated: payload.swapUpdated ?? null,
    swapSource: payload.swapSource ?? "Admin upload",
    highlight: payload.highlight ?? "",
    limitations: Array.isArray(payload.limitations) ? payload.limitations : [],
    _source: "admin",
  };

  return { ok: true, broker };
}

export function parseBrokerUpload(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: "Invalid JSON — check commas, quotes, and brackets." };
  }

  if (Array.isArray(parsed)) {
    if (parsed.length !== 1) {
      return { ok: false, error: "Upload a single broker object, or an array with exactly one broker." };
    }
    return validateBrokerJson(parsed[0]);
  }

  return validateBrokerJson(parsed);
}
